// proceduralLibrary/designs/designGenerators.js
//
// 12 parametric SVG wallpaper "designs" ported from the deck's MOT[0..11]
// (notes5-gif-libraries.elevation.mjs:238-365, DL-063c). Each generator is a
// PURE function of a palette triple [light, deep, accent] -> raw 120x120 SVG
// markup string. Recoloring = pass a different palette (the THEME_STRIP).
//
// No network, no React, no side effects. The tile carries data-iyuga-design so
// a stored data-URI is self-describing (see ../toWallpaperCss).

// ---- safe color handling (mirror of patternGenerators) ----------------------
const SAFE_COLOR_RE =
  /^(?:#[0-9a-fA-F]{3,8}|(?:rgb|rgba|hsl|hsla)\(\s*[0-9.,%\s/]+\))$/;
export const isSafeColor = (c) =>
  typeof c === "string" && SAFE_COLOR_RE.test(c.trim());
const safeColor = (c, fb) => (isSafeColor(c) ? c.trim() : fb);

// Curated palette triples [light, deep, accent] — deck WP[0..9].
export const THEME_PALETTES = [
  { name: "Violet", light: "#B39DFB", deep: "#7C5CF6", accent: "#EDE7FF" },
  { name: "Lagoon", light: "#8FE3F0", deep: "#0E9FBF", accent: "#E6FBFF" },
  { name: "Sunset", light: "#FFC98A", deep: "#EC6F9E", accent: "#FFF0DE" },
  { name: "Meadow", light: "#B7E39A", deep: "#4F9E56", accent: "#EEF9E4" },
  { name: "Blossom", light: "#FBC7E4", deep: "#E7549E", accent: "#FFEBF5" },
  { name: "Amber", light: "#FFE1A3", deep: "#F1922B", accent: "#FFF6E1" },
  { name: "Indigo", light: "#C3CCFF", deep: "#4F5BD6", accent: "#EBEEFF" },
  { name: "Slate", light: "#CBD5E1", deep: "#5A6B85", accent: "#EEF2F7" },
  { name: "Ember", light: "#FFC3B0", deep: "#EE5B44", accent: "#FFEDE7" },
  { name: "Mint", light: "#B9F1DE", deep: "#12A98E", accent: "#E6FBF3" },
];

export const PALETTE_BY_NAME = THEME_PALETTES.reduce((acc, p) => {
  acc[p.name] = p;
  return acc;
}, {});

const DEFAULT_PALETTE = THEME_PALETTES[0];

// Normalise a palette-ish input into a safe [a=light, b=deep, c=accent] triple.
export const normPalette = (pal) => {
  const src = pal || {};
  return [
    safeColor(src.light, DEFAULT_PALETTE.light),
    safeColor(src.deep, DEFAULT_PALETTE.deep),
    safeColor(src.accent, DEFAULT_PALETTE.accent),
  ];
};

const wsvg = (id, inner, paletteMarker) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" ` +
  `viewBox="0 0 120 120" data-iyuga-design="${id}" data-iyuga-palette="${paletteMarker}">${inner}</svg>`;

// ---- design generators (MOT[0..11]) -----------------------------------------

const layeredLandscape = ([a, b, c]) =>
  `<defs><linearGradient id='g' x1='0' y1='0' x2='0' y2='1'><stop offset='0' stop-color='${c}'/><stop offset='1' stop-color='${a}'/></linearGradient></defs>` +
  `<rect width='120' height='120' fill='url(#g)'/>` +
  `<circle cx='82' cy='38' r='16' fill='${c}'/><circle cx='82' cy='38' r='10' fill='#fff' opacity='.55'/>` +
  `<path d='M0 74 26 46 44 66 62 40 84 68 102 52 120 70 V120 H0 Z' fill='${b}' opacity='.38'/>` +
  `<path d='M0 88 22 64 42 84 66 58 88 84 108 68 120 80 V120 H0 Z' fill='${b}' opacity='.62'/>` +
  `<path d='M0 104 30 82 52 100 78 78 100 98 120 88 V120 H0 Z' fill='${b}'/>` +
  `<rect y='70' width='120' height='10' fill='${c}' opacity='.18'/>`;

const auroraRibbons = ([a, b, c]) =>
  `<defs><filter id='bl' x='-30%' y='-30%' width='160%' height='160%'><feGaussianBlur stdDeviation='7'/></filter></defs>` +
  `<rect width='120' height='120' fill='${b}'/>` +
  `<g filter='url(#bl)'><path d='M-10 86 Q22 58 52 74 T130 58 L130 96 Q90 112 52 98 T-10 108 Z' fill='${a}' opacity='.85'/>` +
  `<path d='M-10 58 Q30 30 64 46 T130 30 L130 54 Q88 76 54 62 T-10 76 Z' fill='${c}' opacity='.8'/></g>` +
  `<g fill='${c}'><circle cx='18' cy='18' r='1.6'/><circle cx='46' cy='10' r='1.2'/><circle cx='78' cy='16' r='1.5'/><circle cx='104' cy='9' r='1.1'/><circle cx='96' cy='30' r='1.3'/><circle cx='30' cy='34' r='1.1'/></g>`;

const topography = ([a, b, c]) => {
  let s =
    `<defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='${c}'/><stop offset='1' stop-color='${a}'/></linearGradient></defs>` +
    `<rect width='120' height='120' fill='url(#g)'/><g fill='none' stroke='${b}' stroke-width='1.6' opacity='.85'>`;
  for (let k = 0; k < 5; k++) {
    const rr = 12 + k * 13;
    s += `<path d='M${38 - rr} 44 Q${38 - rr * 0.9} ${44 - rr} 38 ${44 - rr} Q${38 + rr} ${44 - rr * 0.95} ${38 + rr} 44 Q${38 + rr * 1.06} ${44 + rr} 38 ${44 + rr} Q${38 - rr} ${44 + rr * 0.9} ${38 - rr} 44 Z'/>`;
  }
  for (let k = 0; k < 3; k++) {
    const rr = 9 + k * 12;
    s += `<path d='M${98 - rr} 102 Q${98 - rr} ${102 - rr} 98 ${102 - rr} Q${98 + rr * 1.05} ${102 - rr} ${98 + rr} 102 Q${98 + rr} ${102 + rr} 98 ${102 + rr} Q${98 - rr * 0.92} ${102 + rr} ${98 - rr} 102 Z'/>`;
  }
  return s + `</g>`;
};

const terracedBlobs = ([a, b, c]) =>
  `<rect width='120' height='120' fill='${c}'/>` +
  `<path d='M120 0 V120 H28 Q52 96 44 72 Q37 50 66 40 Q96 30 92 0 Z' fill='${a}' opacity='.45'/>` +
  `<path d='M120 0 V120 H58 Q76 98 68 78 Q60 58 86 46 Q110 35 106 0 Z' fill='${a}' opacity='.75'/>` +
  `<path d='M120 8 V120 H86 Q98 100 90 84 Q83 68 102 58 Q118 49 120 8 Z' fill='${b}'/>`;

const lowPoly = ([a, b, c]) =>
  `<rect width='120' height='120' fill='${a}'/>` +
  `<path d='M0 0 60 0 0 60Z' fill='${b}' opacity='.9'/><path d='M120 0 60 0 120 60Z' fill='${c}'/>` +
  `<path d='M0 120 0 60 60 120Z' fill='${c}'/><path d='M120 120 120 60 60 120Z' fill='${b}' opacity='.9'/>` +
  `<path d='M60 0 120 60 60 120 0 60Z' fill='${b}' opacity='.45'/>`;

const organicBlobs = ([a, b, c]) =>
  `<rect width='120' height='120' fill='${c}'/>` +
  `<path d='M20 40 Q10 10 45 15 Q90 20 95 45 Q100 75 65 90 Q25 105 18 60 Z' fill='${a}' opacity='.92'/>` +
  `<ellipse cx='86' cy='86' rx='34' ry='27' fill='${b}' opacity='.85'/>`;

const bokehGlow = ([a, b, c]) =>
  `<defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='${b}'/><stop offset='1' stop-color='${a}'/></linearGradient>` +
  `<filter id='bl' x='-40%' y='-40%' width='180%' height='180%'><feGaussianBlur stdDeviation='4'/></filter></defs>` +
  `<rect width='120' height='120' fill='url(#g)'/>` +
  `<g filter='url(#bl)' fill='${c}'><circle cx='26' cy='30' r='15' opacity='.55'/><circle cx='92' cy='22' r='10' opacity='.4'/><circle cx='72' cy='66' r='18' opacity='.35'/><circle cx='24' cy='92' r='11' opacity='.45'/><circle cx='104' cy='96' r='14' opacity='.5'/></g>` +
  `<g fill='${c}'><circle cx='58' cy='24' r='2.4'/><circle cx='100' cy='54' r='2'/><circle cx='44' cy='58' r='1.8'/><circle cx='60' cy='102' r='2.2'/><circle cx='16' cy='62' r='1.6'/></g>`;

const waveBands = ([a, b, c]) =>
  `<rect width='120' height='120' fill='${a}'/>` +
  `<path d='M0 44 Q30 24 60 44 T120 44 V0 H0 Z' fill='${c}' opacity='.85'/>` +
  `<path d='M0 74 Q30 54 60 74 T120 74 V40 H0 Z' fill='${b}' opacity='.6'/>` +
  `<path d='M0 104 Q30 84 60 104 T120 104 V74 H0 Z' fill='${b}' opacity='.9'/>`;

const auroraMesh = ([a, b, c]) =>
  `<defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='${a}'/><stop offset='1' stop-color='${b}'/></linearGradient><filter id='bl'><feGaussianBlur stdDeviation='14'/></filter></defs>` +
  `<rect width='120' height='120' fill='url(#g)'/>` +
  `<g opacity='.7' filter='url(#bl)'><circle cx='30' cy='30' r='46' fill='${c}'/><circle cx='96' cy='54' r='38' fill='${a}'/><circle cx='58' cy='102' r='42' fill='${b}'/></g>`;

const quarterArcs = ([a, b, c]) =>
  `<rect width='120' height='120' fill='${a}'/>` +
  `<path d='M0 0 A120 120 0 0 1 120 120 L0 120 Z' fill='${b}' opacity='.32'/>` +
  `<path d='M120 0 A120 120 0 0 0 0 120' fill='none' stroke='${c}' stroke-width='10' opacity='.85'/>` +
  `<circle cx='120' cy='0' r='30' fill='${c}'/>`;

const duneShadows = ([a, b, c]) =>
  `<defs><linearGradient id='g' x1='0' y1='0' x2='0' y2='1'><stop offset='0' stop-color='${c}'/><stop offset='.62' stop-color='${a}'/></linearGradient></defs>` +
  `<rect width='120' height='120' fill='url(#g)'/>` +
  `<circle cx='88' cy='24' r='10' fill='#fff' opacity='.8'/>` +
  `<path d='M0 72 Q42 50 76 74 L76 120 H0 Z' fill='${a}'/>` +
  `<path d='M76 74 Q98 89 120 82 V120 H76 Z' fill='${b}' opacity='.72'/>` +
  `<path d='M0 97 Q44 84 78 98 L78 120 H0 Z' fill='${b}' opacity='.4'/>` +
  `<path d='M78 98 Q100 106 120 100 V120 H78 Z' fill='${b}'/>`;

const nightSky = ([a, b, c]) =>
  `<defs><linearGradient id='g' x1='0' y1='0' x2='0' y2='1'><stop offset='0' stop-color='#101A3C'/><stop offset='1' stop-color='${b}'/></linearGradient></defs>` +
  `<rect width='120' height='120' fill='url(#g)'/>` +
  `<circle cx='90' cy='30' r='11' fill='${c}'/><circle cx='90' cy='30' r='16' fill='none' stroke='${c}' stroke-width='2' opacity='.3'/>` +
  `<g fill='${c}'><circle cx='18' cy='18' r='1.6'/><circle cx='40' cy='36' r='1.2'/><circle cx='60' cy='14' r='1.4'/><circle cx='28' cy='56' r='1.3'/><circle cx='70' cy='52' r='1.1'/><circle cx='12' cy='40' r='1'/><circle cx='50' cy='68' r='1.2'/><circle cx='106' cy='64' r='1.3'/></g>` +
  `<path d='M0 92 Q30 78 58 90 Q88 102 120 88 V120 H0 Z' fill='#0B1230' opacity='.85'/>` +
  `<path d='M0 104 Q40 94 74 104 Q100 110 120 104 V120 H0 Z' fill='${a}' opacity='.3'/>`;

// ---- registry ---------------------------------------------------------------
// [id, label, generator, defaultPaletteName] — order = deck DESIGNS list.
export const DESIGNS = [
  { id: "mountainDawn", label: "Mountain dawn", generator: layeredLandscape, palette: "Violet" },
  { id: "auroraRibbons", label: "Aurora ribbons", generator: auroraRibbons, palette: "Lagoon" },
  { id: "topography", label: "Topography", generator: topography, palette: "Sunset" },
  { id: "terracedBlobs", label: "Terraced blobs", generator: terracedBlobs, palette: "Meadow" },
  { id: "lowPolyFacets", label: "Low-poly facets", generator: lowPoly, palette: "Indigo" },
  { id: "organicBlobs", label: "Organic blobs", generator: organicBlobs, palette: "Blossom" },
  { id: "bokehGlow", label: "Bokeh glow", generator: bokehGlow, palette: "Ember" },
  { id: "waveBands", label: "Wave bands", generator: waveBands, palette: "Mint" },
  { id: "auroraMesh", label: "Aurora mesh", generator: auroraMesh, palette: "Slate" },
  { id: "quarterArcs", label: "Quarter arcs", generator: quarterArcs, palette: "Lagoon" },
  { id: "duneShadows", label: "Dune shadows", generator: duneShadows, palette: "Amber" },
  { id: "nightSky", label: "Night sky", generator: nightSky, palette: "Indigo" },
];

export const DESIGN_BY_ID = DESIGNS.reduce((acc, d) => {
  acc[d.id] = d;
  return acc;
}, {});

// Generate the SVG markup for a design id + palette. Returns "" for unknown ids.
// Bakes a `data-iyuga-palette` marker naming which palette produced this SVG —
// its curated NAME when `palette` is one of THEME_PALETTES (carries `.name`),
// or its exact `custom:light|deep|accent` hex triple when it's a derived
// Segment/Wheel match-pill palette (unnamed) — so a stored CSS string can be
// parsed back to the palette the user actually applied (see parseDesignCss in
// ../toWallpaperCss), not silently reset to the design's hardcoded default.
export const generateDesignSvg = (id, palette) => {
  const entry = DESIGN_BY_ID[id];
  if (!entry) return "";
  const pal = palette || PALETTE_BY_NAME[entry.palette] || DEFAULT_PALETTE;
  const normalized = normPalette(pal);
  const marker =
    pal && pal.name
      ? pal.name
      : `custom:${normalized[0]}|${normalized[1]}|${normalized[2]}`;
  return wsvg(id, entry.generator(normalized), marker);
};
